import { CloudRain, Cloud, Zap } from 'lucide-react';
import type { MinutelyBlock, NearestStorm } from '../types';

export default function MinutelyPrecip({
  minutely,
  nearestStorm,
}: {
  minutely: MinutelyBlock | null;
  nearestStorm: NearestStorm | null;
}) {
  if (!minutely) return null;
  const maxIntensity = Math.max(0.05, ...minutely.minutes.map((m) => m.intensity));
  const hasPrecip = minutely.minutes.some((m) => m.intensity > 0 || m.probabilityPct > 10);
  const bearingLabel = nearestStorm?.bearingDeg != null
    ? compassFromDeg(nearestStorm.bearingDeg)
    : null;

  return (
    <section>
      <h2 className="text-xs uppercase tracking-wider opacity-70 mb-2 flex items-center gap-2">
        {hasPrecip ? <CloudRain className="w-3.5 h-3.5" /> : <Cloud className="w-3.5 h-3.5" />}
        Next 60 min
      </h2>
      <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3">
        <div className="text-sm">{minutely.summary || 'Clear conditions.'}</div>

        {hasPrecip && (
          <div className="mt-3 flex items-end gap-px h-14">
            {minutely.minutes.map((m) => {
              const h = Math.min(100, (m.intensity / maxIntensity) * 100);
              const opacity = 0.3 + Math.min(0.7, m.probabilityPct / 100);
              return (
                <div
                  key={m.time}
                  className="flex-1 bg-sky-300 rounded-sm"
                  style={{ height: `${Math.max(2, h)}%`, opacity }}
                  title={`${m.probabilityPct}% chance, ${m.intensity.toFixed(3)} in/hr`}
                />
              );
            })}
          </div>
        )}

        {nearestStorm && (
          <div className="mt-3 flex items-center gap-2 text-xs border-t border-white/10 pt-2">
            <Zap className="w-3.5 h-3.5 text-amber-300" />
            Nearest storm cell: {nearestStorm.distanceMi} mi
            {bearingLabel ? ` ${bearingLabel}` : ''}
          </div>
        )}
      </div>
    </section>
  );
}

function compassFromDeg(deg: number): string {
  const dirs = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'];
  return dirs[Math.round(deg / 22.5) % 16];
}
